import { useCallback, useEffect, useState } from "react"

export type Lang = "en" | "it"

const STORAGE_KEY = "eyesight:lang"

function systemDefault(): Lang {
  if (typeof navigator === "undefined") return "en"
  const tag = (navigator.language || "en").toLowerCase()
  return tag.startsWith("it") ? "it" : "en"
}

function readStored(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === "en" || v === "it" ? v : null
  } catch {
    return null
  }
}

// Translation dictionary. Keys are short identifiers in English.
// Add new strings here when you encounter UI copy that needs to vary by lang.
export const STRINGS = {
  // Shell / nav
  nav_home: { en: "Home", it: "Home" },
  nav_receipts: { en: "Receipts", it: "Scontrini" },
  nav_diary: { en: "Diary", it: "Diario" },
  nav_settings: { en: "Settings", it: "Impostazioni" },
  signout: { en: "Sign out", it: "Esci" },
  by_qsay: { en: "by Q-Say", it: "by Q-Say" },
  switch_theme_light: { en: "Switch to light theme", it: "Passa al tema chiaro" },
  switch_theme_dark: { en: "Switch to dark theme", it: "Passa al tema scuro" },
  theme_light: { en: "Light", it: "Chiaro" },
  theme_dark: { en: "Dark", it: "Scuro" },
  switch_lang_en: { en: "Switch to English", it: "Passa all'inglese" },
  switch_lang_it: { en: "Switch to Italian", it: "Passa all'italiano" },
  footer_left: {
    en: "Eyesight is a product of Strategoi, part of the Strategoi Q-Say suite.",
    it: "Eyesight è un prodotto di Strategoi, parte della suite Strategoi Q-Say.",
  },
  footer_right: {
    en: "Based on the italian survey ECOICOP — Italian Household Budget Survey (ISTAT 1559)",
    it: "Based on the italian survey ECOICOP — Indagine sulle spese delle famiglie (ISTAT 1559)",
  },

  // Home page
  hi: { en: "Hi", it: "Ciao" },
  home_intro: {
    en: "Upload your receipts, the AI reads and classifies them into your Household Budget Survey diary. Edit manually if needed. Eyesight is a public showcase by the Q-Say platform.",
    it: "Carica gli scontrini: l'AI legge e classifica le voci nel tuo diario dell'Indagine sulle spese delle famiglie. Puoi modificare a mano se serve. Eyesight è uno showcase pubblico della piattaforma Q-Say.",
  },
  card_new_receipt: { en: "New receipt", it: "Nuovo scontrino" },
  card_upload_photo: { en: "Upload a photo", it: "Carica una foto" },
  card_current_week_diary: { en: "Current week diary", it: "Diario settimana corrente" },
  latest_receipts: { en: "Latest receipts", it: "Ultimi scontrini" },
  loading: { en: "Loading…", it: "Caricamento…" },
  nothing_yet: { en: "Nothing yet. Upload one.", it: "Niente ancora. Caricane uno." },
  error_prefix: { en: "Error", it: "Errore" },

  // Receipts page
  receipts_title: { en: "Receipts", it: "Scontrini" },
  drag_or_tap: { en: "Drag a file here or tap to capture.", it: "Trascina un file o tocca per scattare." },
  uploading: { en: "Uploading…", it: "Carico…" },
  extracting: { en: "Extracting…", it: "Estraggo…" },
  error_title: { en: "Something went wrong", it: "Qualcosa è andato storto" },
  upload_failed: { en: "Could not process the receipt", it: "Impossibile elaborare lo scontrino" },
  error_status: { en: "Status", it: "Stato" },
  error_detail: { en: "Detail", it: "Dettaglio" },
  error_close: { en: "Close", it: "Chiudi" },
  error_retry: { en: "Retry", it: "Riprova" },
  history: { en: "History", it: "Storico" },
  none_yet: { en: "No receipts yet.", it: "Nessuno scontrino ancora." },
  th_date: { en: "Date", it: "Data" },
  th_merchant: { en: "Merchant", it: "Esercente" },
  th_total: { en: "Total", it: "Totale" },
  th_status: { en: "Status", it: "Stato" },
  th_actions: { en: "", it: "" },
  delete: { en: "Delete", it: "Elimina" },
  confirm_delete_receipt: {
    en: "Delete this receipt and all linked entries?",
    it: "Eliminare questo scontrino e tutte le voci collegate?",
  },
  unsupported_type: { en: "Unsupported type", it: "Tipo non supportato" },
  too_large: { en: "File too large", it: "File troppo grande" },

  // Receipt detail
  no_merchant_receipt: { en: "Receipt with no merchant", it: "Scontrino senza esercente" },
  th_state: { en: "Status", it: "Stato" },
  th_lines: { en: "Lines", it: "Righe" },
  th_s3_key: { en: "S3 key", it: "S3 key" },
  receipt_detail_hint: {
    en: "Extracted lines appear in the diary for the matching week.",
    it: "Le righe estratte compaiono nel diario della settimana corrispondente.",
  },
  not_found: { en: "Receipt not found", it: "Scontrino non trovato" },

  // Diary book
  diary_title_with_week: { en: "HBS Diary — ", it: "Diario HBS — " },
  istat_subtitle: { en: "Official ISTAT/SPE/1 booklet · 84 pages", it: "Fascicolo ufficiale ISTAT/SPE/1 · 84 pagine" },
  loading_diary: { en: "Loading the diary…", it: "Carico il diario…" },
  loading_template: { en: "Loading template…", it: "Carico il template…" },
  unable_pdf: { en: "Unable to load the PDF.", it: "Impossibile caricare il PDF." },
  summary: { en: "Summary", it: "Sommario" },
  group_front: { en: "Front matter", it: "Apertura" },
  group_week1: { en: "Week 1 (Days 1–7)", it: "Settimana 1 (Giorni 1–7)" },
  group_week2: { en: "Week 2 (Days 8–14)", it: "Settimana 2 (Giorni 8–14)" },
  group_end: { en: "End matter", it: "Chiusura" },
  week_entries: { en: "Week entries", it: "Voci della settimana" },
  week_total: { en: "Week total", it: "Totale settimana" },
  no_entries_day: { en: "No entries yet.", it: "Nessuna voce." },
  add_receipt: { en: "Add receipt", it: "Aggiungi scontrino" },

  // Assistant drawer
  assistant_title: { en: "Eyesight Assistant", it: "Eyesight Assistant" },
  assistant_ready: { en: "Ready", it: "Pronto" },
  assistant_responding: { en: "Responding…", it: "Sta rispondendo…" },
  assistant_connecting: { en: "Connecting…", it: "Connessione…" },
  assistant_connection_lost: { en: "Connection lost", it: "Connessione persa" },
  assistant_clear: { en: "Clear", it: "Pulisci" },
  assistant_type_here: { en: "Type here…", it: "Scrivi qui…" },
  assistant_send: { en: "Send", it: "Invia" },
  assistant_empty_hint: {
    en: "Ask anything about your diary, for example:\n«Summarise last week's groceries»\n«Any spending that looks off?»",
    it: "Chiedi qualcosa sul tuo diario, ad esempio:\n«Riassumi gli alimentari della scorsa settimana»\n«Hai notato spese fuori controllo?»",
  },

  // Settings
  settings_title: { en: "Settings", it: "Impostazioni" },
  settings_account: { en: "Account", it: "Account" },
  settings_data_section: { en: "Your data", it: "I tuoi dati" },
  settings_export_desc: {
    en: "Export all your data (profile, receipts, diary entries, images) as a single ZIP. The link is valid for 1 hour.",
    it: "Esporta tutti i tuoi dati (profilo, scontrini, voci del diario, immagini) in un singolo ZIP. Il link è valido un'ora.",
  },
  settings_export_btn: { en: "Export my data", it: "Esporta i miei dati" },
  settings_generating: { en: "Generating…", it: "Genero…" },
  settings_danger: { en: "Danger zone", it: "Zona pericolosa" },
  settings_delete_desc: {
    en: "Delete your account. We do a soft delete: data stays for 30 days (in case you change your mind) and is then removed.",
    it: "Elimina il tuo account. Soft-delete: i dati restano 30 giorni (in caso di ripensamento) e poi vengono rimossi.",
  },
  settings_delete_btn: { en: "Delete my account", it: "Elimina il mio account" },
  settings_deleting: { en: "Deleting…", it: "Eliminazione…" },
  settings_confirm_delete: {
    en: "Are you sure you want to delete your account? This is a soft delete: data is retained for 30 days, then permanently removed.",
    it: "Confermi la cancellazione dell'account? Soft-delete: i dati restano 30 giorni, poi vengono rimossi.",
  },
  settings_export_ok: {
    en: "Ready. {rows} records + {receipts} images. The link should open in a new tab.",
    it: "Pronto. {rows} record + {receipts} immagini. Il link si aprirà in una nuova scheda.",
  },
  settings_deleted_ok: {
    en: "Account deleted. Signing you out…",
    it: "Account cancellato. Disconnessione in corso…",
  },

  // Login callback / Protected
  redirecting_signin: { en: "Redirecting to sign-in…", it: "Reindirizzo al login…" },
  login_complete: { en: "Completing sign-in…", it: "Completamento login…" },
  login_failed: { en: "Sign-in failed", it: "Login fallito" },
  auth_error: { en: "Authentication error", it: "Errore autenticazione" },

  // Diary export / button
  open_interactive_diary: { en: "Open interactive diary", it: "Apri diario interattivo" },
  export_pdf: { en: "Export PDF", it: "Esporta PDF" },

  // Templates
  nav_templates: { en: "Templates", it: "Modelli" },
  templates_title: { en: "Templates", it: "Modelli" },
  templates_intro: {
    en: "Pick a fillable form to start, or upload your own PDF template.",
    it: "Scegli un modulo compilabile, oppure carica il tuo PDF.",
  },
  templates_upload: { en: "Upload new template", it: "Carica nuovo modello" },
  templates_my: { en: "My templates", it: "I miei modelli" },
  templates_public: { en: "Public templates", it: "Modelli pubblici" },
  templates_pages: { en: "{n} pages", it: "{n} pagine" },
  templates_open: { en: "Open", it: "Apri" },

  // Upload
  upload_title: { en: "Upload a fillable template", it: "Carica un modello compilabile" },
  upload_id: { en: "Template ID", it: "ID modello" },
  upload_id_hint: { en: "Lowercase, digits, dashes. 3–60 chars.", it: "Minuscolo, numeri, trattini. 3–60 caratteri." },
  upload_name: { en: "Display name", it: "Nome visualizzato" },
  upload_version: { en: "Version", it: "Versione" },
  upload_lang: { en: "Language", it: "Lingua" },
  upload_pdf: { en: "PDF file", it: "File PDF" },
  upload_cells: { en: "Cells JSON", it: "JSON celle" },
  upload_sections: { en: "Sections JSON", it: "JSON sezioni" },
  upload_mapping: { en: "Mapping JSON", it: "JSON mapping" },
  upload_public: { en: "Public", it: "Pubblico" },
  upload_submit: { en: "Upload", it: "Carica" },
  upload_saving: { en: "Uploading…", it: "Carico…" },
  upload_help: {
    en: "Upload any fillable PDF + a cells JSON describing each input box (code, x, y, w, h in 0–1 fractions of the page).",
    it: "Carica un PDF compilabile + un JSON che descrive ogni campo (codice, x, y, w, h come frazioni 0–1 della pagina).",
  },
  upload_no_file: { en: "Choose a PDF…", it: "Scegli un PDF…" },

  // Save indicator
  saving: { en: "Saving…", it: "Salvo…" },
  saved_ago: { en: "Saved", it: "Salvato" },
  save_error: { en: "Save failed", it: "Salvataggio fallito" },
} as const

export type StringKey = keyof typeof STRINGS

let listeners: Array<(l: Lang) => void> = []
let currentLang: Lang | null = null

function ensureInitialised(): Lang {
  if (currentLang === null) {
    currentLang = readStored() ?? systemDefault()
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", currentLang)
    }
  }
  return currentLang
}

function setLang(next: Lang) {
  currentLang = next
  try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", next)
  }
  listeners.forEach((cb) => cb(next))
}

export function useT(): {
  lang: Lang
  t: (key: StringKey, vars?: Record<string, string | number>) => string
  set: (l: Lang) => void
  toggle: () => void
} {
  const [lang, setLocal] = useState<Lang>(() => ensureInitialised())

  useEffect(() => {
    const cb = (l: Lang) => setLocal(l)
    listeners.push(cb)
    return () => {
      listeners = listeners.filter((x) => x !== cb)
    }
  }, [])

  const t = useCallback((key: StringKey, vars?: Record<string, string | number>) => {
    const entry = STRINGS[key]
    const raw = entry ? entry[lang] : String(key)
    if (!vars) return raw
    return Object.entries(vars).reduce((acc, [k, v]) =>
      acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)), raw)
  }, [lang])

  return {
    lang,
    t,
    set: setLang,
    toggle: () => setLang(lang === "en" ? "it" : "en"),
  }
}
